import { defer, type Deferred } from './smartpromise.classes.deferred.js';

/**
 * The largest delay setTimeout accepts. Longer delays fire after 1 ms in Node.js and
 * browsers, so Timeout waits for them in chunks of at most this size.
 */
const maxTimerDelayMs = 2_147_483_647;

type TTimer = ReturnType<typeof setTimeout>;

/**
 * Node.js timers can be unrefed; browser and Deno timer handles are plain numbers without unref.
 */
const unrefTimer = (timerArg: TTimer) => {
  (timerArg as TTimer & { unref?: () => void }).unref?.();
};

/**
 * A promise-backed timer that can be cancelled and unrefed.
 * `promise` resolves with the pass-on value once the delay has elapsed;
 * a cancelled Timeout never settles its promise.
 */
export class Timeout<T> {
  public promise: Promise<T>;

  private deferred: Deferred<T>;
  private passOn: T | undefined;
  private timer: TTimer;
  private unrefed = false;
  private state: 'running' | 'completed' | 'cancelled' = 'running';
  /**
   * monotonic deadline, so wall-clock adjustments do not skew getTimeLeft()
   */
  private deadline: number;
  private timeLeftAtCancel = 0;

  constructor(timeInMillisecondArg: number, passOn?: T, unrefedArg = false) {
    this.passOn = passOn;
    this.deferred = defer<T>();
    this.promise = this.deferred.promise;
    this.deadline = performance.now() + timeInMillisecondArg;
    this.timer = this.startChunk(timeInMillisecondArg);
    if (unrefedArg) {
      this.makeUnrefed();
    }
  }

  /**
   * starts the one live timer for the next chunk of the remaining delay
   */
  private startChunk(remainingMillisArg: number): TTimer {
    const chunkMillis = Math.min(remainingMillisArg, maxTimerDelayMs);
    const chunkTimer = setTimeout(() => {
      if (this.state !== 'running') {
        return;
      }
      const remainingMillis = remainingMillisArg - chunkMillis;
      if (remainingMillis > 0) {
        this.timer = this.startChunk(remainingMillis);
      } else {
        this.state = 'completed';
        this.deferred.resolve(this.passOn);
      }
    }, chunkMillis);
    if (this.unrefed) {
      unrefTimer(chunkTimer);
    }
    return chunkTimer;
  }

  /**
   * unreffing a timeout causes the node process to not wait for completion before exit;
   * it applies to every remaining chunk of a long delay
   */
  public makeUnrefed() {
    this.unrefed = true;
    unrefTimer(this.timer);
  }

  /**
   * cancels the timer; the promise stays pending and getTimeLeft() keeps the time
   * that was left at cancellation. Cancelling a completed or cancelled Timeout does nothing.
   */
  public cancel() {
    if (this.state !== 'running') {
      return;
    }
    this.timeLeftAtCancel = this.getTimeLeft();
    this.state = 'cancelled';
    clearTimeout(this.timer);
  }

  /**
   * the whole milliseconds left until the promise resolves; 0 once it has resolved
   */
  public getTimeLeft(): number {
    if (this.state === 'completed') {
      return 0;
    }
    if (this.state === 'cancelled') {
      return this.timeLeftAtCancel;
    }
    const timeLeft = Math.ceil(this.deadline - performance.now());
    return timeLeft > 0 ? timeLeft : 0;
  }
}
